import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { Ed, Reveal } from "../Editorial";
import { PromoBanner } from "./PromoBanner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Precios de clase suelta. Espejan RIDE_PRICE y FLOOR_PRICE del backend.
const SINGLE_PRICE = 17.99;   // spinning, con Glory
const FLOOR_PRICE = 15.99;    // aeróbicos y dancefit, con Graciela

const FALLBACK_PACKAGES = [
  { id: "classes8", covers: "ride", classes: 8, name: { en: "8 Classes", es: "8 Clases" }, price: 135, unit: "month",
    note: { en: "8 spinning classes to use within 1 month.", es: "8 clases de spinning para usar en 1 mes." } },
  { id: "classes12", covers: "ride", classes: 12, name: { en: "12 Classes", es: "12 Clases" }, price: 195, unit: "month",
    note: { en: "12 spinning classes to use within 1 month.", es: "12 clases de spinning para usar en 1 mes." } },
  { id: "classes16", covers: "ride", classes: 16, name: { en: "16 Classes", es: "16 Clases" }, price: 240, unit: "month",
    note: { en: "16 spinning classes to use within 1 month.", es: "16 clases de spinning para usar en 1 mes." } },
  { id: "floor12", covers: "floor", classes: 12, name: { en: "12 Floor Classes", es: "12 Clases de Suelo" }, price: 170, unit: "month",
    note: { en: "12 aerobics or dancefit classes to use within 1 month.", es: "12 clases de aeróbicos o dancefit para usar en 1 mes." } },
  { id: "floor20", covers: "floor", classes: 20, name: { en: "20 Floor Classes", es: "20 Clases de Suelo" }, price: 240, unit: "month",
    note: { en: "20 aerobics or dancefit classes to use within 1 month.", es: "20 clases de aeróbicos o dancefit para usar en 1 mes." } },
  { id: "unlimited", covers: "any", classes: null, name: { en: "Unlimited", es: "Ilimitado" }, price: 330, unit: "month",
    note: { en: "Every class, spinning and floor, for 1 month. One spot per class.", es: "Todas las clases, spinning y suelo, por 1 mes. Un lugar por clase." } },
];

export const PricingSection = () => {
    const { lang, t } = useLang();
    const { isAuth, purchaseMembership } = useAuth();
    const navigate = useNavigate();
    const [packages, setPackages] = useState(FALLBACK_PACKAGES);
    const [comprando, setComprando] = useState(null);

    // El paquete va asociado a una persona, así que hace falta cuenta. Si ya
    // hay sesión se va directo al pago; si no, a la página de cuenta.
    const comprar = async (id) => {
          if (!isAuth) {
                navigate("/account");
                return;
          }
          setComprando(id);
          try {
                await purchaseMembership(id);
          } catch (e) {
                setComprando(null);
          }
    };

    useEffect(() => {
          axios
            .get(`${API}/packages`)
            .then((r) => setPackages(Array.isArray(r.data) && r.data.length ? r.data : FALLBACK_PACKAGES))
            .catch(() => {});
    }, []);

    const unitLabel = (unit) =>
          unit === "class" ? t.pricing.perClass : unit === "month" ? t.pricing.perMonth : t.pricing.oneTime;

    return (
          <section id="pricing" className="px-6 md:px-16 py-24 md:py-40" data-testid="pricing-section">
                <Reveal>
                        <p className="overline-gold font-sans">
                                  <Ed text={t.pricing.overline} goldItalic={false} />
                        </p>
                        <h2 className="mt-8 font-serif text-4xl md:text-6xl text-white">
                                  <Ed text={t.pricing.heading} />
                        </h2>
                </Reveal>

                <PromoBanner />

                <Reveal className="mt-12 md:mt-16">
                        <div
                          data-testid="single-class-price"
                          className="flex flex-wrap items-end justify-between gap-4 border border-[#C5A059]/30 bg-[#C5A059]/[0.04] px-6 md:px-9 py-7"
                        >
                                <div>
                                        <h3 className="font-serif text-2xl md:text-3xl text-white">{t.pricing.singleName}</h3>
                                        <p className="mt-1 text-sm text-zinc-500 font-light">{t.pricing.singleNote}</p>
                                </div>
                                <p className="font-serif text-4xl md:text-5xl text-white leading-none">
                                        <span className="text-2xl align-top text-[#C5A059]">$</span>{SINGLE_PRICE}
                                        <span className="ml-3 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] align-middle">{t.pricing.perClass}</span>
                                </p>
                        </div>
                </Reveal>

                <Reveal className="mt-5">
                        <div
                          data-testid="floor-class-price"
                          className="flex flex-wrap items-end justify-between gap-4 border border-white/10 px-6 md:px-9 py-7"
                        >
                                <div>
                                        <h3 className="font-serif text-2xl md:text-3xl text-white">{t.pricing.floorName}</h3>
                                        <p className="mt-1 text-sm text-zinc-500 font-light">{t.pricing.floorNote}</p>
                                </div>
                                <p className="font-serif text-4xl md:text-5xl text-white leading-none">
                                        <span className="text-2xl align-top text-[#C5A059]">$</span>{FLOOR_PRICE}
                                        <span className="ml-3 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] align-middle">{t.pricing.perClass}</span>
                                </p>
                        </div>
                </Reveal>

                <Reveal className="mt-12">
                        <p className="overline-gold font-sans">{t.pricing.orPackages}</p>
                </Reveal>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                  {packages.map((p, i) => (
                      <Reveal
                                    key={p.id}
                                    delay={i * 0.1}
                                    className="border-t border-white/10 hover:border-[#C5A059] transition-colors duration-500 p-8 md:p-10"
                                  >
                                  <div data-testid={`package-${p.id}`}>
                                                <p className="text-[0.55rem] uppercase tracking-[0.3em] text-[#C5A059]">{t.pricing.covers[p.covers] || ""}</p>
                                                <h3 className="mt-3 font-serif text-2xl text-white">{p.name[lang]}</h3>
                                                <p className="mt-8 font-serif text-5xl text-white">
                                                                <span className="text-2xl align-top text-[#C5A059]">$</span>
                                                  {p.price}
                                                </p>
                                                <p className="mt-2 text-[0.65rem] uppercase tracking-[0.3em] text-[#C5A059]">{unitLabel(p.unit)}</p>
                                                <p className="mt-6 text-sm text-zinc-500 font-light leading-relaxed">{p.note[lang]}</p>
                                                <button
                                                  data-testid={`buy-${p.id}`}
                                                  onClick={() => comprar(p.id)}
                                                  disabled={comprando === p.id}
                                                  className="mt-7 w-full border border-[#C5A059]/50 px-5 py-3 text-[0.6rem] uppercase tracking-[0.3em] text-[#C5A059] transition-colors duration-500 hover:bg-[#C5A059] hover:text-black disabled:opacity-40"
                                                >
                                                  {comprando === p.id ? t.pricing.buying : t.pricing.buy}
                                                </button>
                                  </div>
                      </Reveal>
                    ))}
                </div>
          
                <Reveal className="mt-12">
                        <p className="text-sm text-zinc-500 font-light leading-relaxed max-w-xl">{t.pricing.packageNote}</p>
                        <p className="mt-4 text-sm text-zinc-500 font-light italic font-serif">{t.pricing.note}</p>
                </Reveal>

                <Reveal className="mt-14 border-t border-white/10 pt-10 max-w-xl">
                        <p className="overline-gold font-sans">{t.pricing.policyTitle}</p>
                        <ul className="mt-6 space-y-3">
                          {t.pricing.policy.map((line, i) => (
                            <li key={i} className="flex items-start gap-3 text-sm text-zinc-400 font-light leading-relaxed">
                              <span className="mt-[0.55rem] h-[3px] w-[3px] shrink-0 rounded-full bg-[#C5A059]" />
                              {line}
                            </li>
                          ))}
                        </ul>
                </Reveal>
          </section>
        );
};
